import { CreateNewRoute } from '@/javascript/domain/usecases/maps/create-new-route'
import { AccessDeniedError, UnexpectedError, ValidationError } from '@/javascript/domain/errors'
import { HttpClient, HttpStatusCode } from '@/javascript/data/protocols/http'
import { RemoteRoutePointType } from '@/javascript/data/entities/maps/remote-route-point'

export class RemoteCreateNewRoute implements CreateNewRoute {
  constructor(
    private readonly url: string,
    private readonly httpClient: HttpClient<RemoteCreateNewRoute.Response>
  ) {}

  async create(params: CreateNewRoute.Params): Promise<void> {
    const suggestionByMemberId = new Map(params.routeSuggestions.map(s => [s.reference, s]))
    const httpResponse = await this.httpClient.request({
      url: this.url,
      method: 'post',
      body: JSON.stringify({
        date: params.date.toISOString(),
        members_attributes: params.memberById.size > 0
          ? Array.from(params.memberById.values()).flatMap(m => {
              const suggestion = suggestionByMemberId.get(m.id.toString())
              if (!suggestion) return []
              return [{
                account_member_id: m.id,
                travel_time_seconds: suggestion.travelTime,
                total_time_seconds: suggestion.totalTime,
                total_distance_meters: suggestion.totalDistance,
                service_time_seconds: suggestion.serviceTime,
                points_count: suggestion.points.length,
                origin_lat: suggestion.origin.lat,
                origin_lng: suggestion.origin.lng,
                origin_address: m.configuration.initial.address,
                destination_lat: suggestion.destination.lat,
                destination_lng: suggestion.destination.lng,
                destination_address: m.configuration.final.address,
                geometry: JSON.stringify(suggestion.geometry),
                points_attributes: suggestion.points.map(p => ({
                  sequence: p.sequence,
                  point_type: p.type === 'vehicle-origin' ? RemoteRoutePointType.origin : p.type === 'vehicle-destination' ? RemoteRoutePointType.destination : RemoteRoutePointType.waypoint,
                  work_id: p.type === 'service-delivery' ? p.reference : null,
                  arrival_time: p.arrivalTime,
                  departure_time: p.departureTime,
                  location_lat: p.location.lat,
                  location_lng: p.location.lng,
                  location_address: p.location.address
                }))
              }]
            })
          : []
      })
    })
    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
      case HttpStatusCode.noContent: return
      case HttpStatusCode.unprocessableEntity: throw new ValidationError({ message: httpResponse.body.error })
      case HttpStatusCode.forbidden: throw new AccessDeniedError()
      default: throw new UnexpectedError()
    }
  }
}

export namespace RemoteCreateNewRoute {
  export type Response = {
    error?: string
  }
}
